import { Fragment } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import MuiDataGrid from 'components/MuiDataGrid';
import { QuillViewer } from 'components/QuillEditor';
import { IUpedateNursingRecord } from 'apis/survey/type';
import useI18n from 'hooks/useI18n';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { useLocation } from 'react-router-dom';

interface Props {
  list: IUpedateNursingRecord[];
  isLoading: boolean;
  totalCount: number;
  page: number;
  onPageChange: (page: number) => void;
}

/** record_type → locale 접두사 */
const RECORD_LOCALE_PREFIX: Record<number, string> = {
  0: 'NANDA',
  1: 'SOAPIE',
  2: 'FOCUSDAR',
  3: 'NARRATIVE',
  4: 'REMARKS',
  5: 'REMARKS',
  6: 'OMAHA',
  7: 'ICNP',
  8: 'ICNPBROWSER',
};

function ContentsBody(props: Props) {
  const i18n = useI18n();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const pageSize = params.get('size');

  const { palette } = useTheme();

  const { list, isLoading, totalCount, page, onPageChange } = props;

  /**
   * 항목 라벨은 반드시 저장된 key로 찾는다.
   * 빈 항목은 저장 시 content JSON에서 빠지므로(utils/convert findKeyValue),
   * 순번(index)으로 라벨을 붙이면 학생이 비워 둔 항목만큼 라벨이 앞으로 당겨진다.
   */
  const fieldLabel = (recordType: number, fieldKey: string) =>
    i18n(`${RECORD_LOCALE_PREFIX[recordType] ?? 'NANDA'}.${fieldKey}` as any);

  const columns: GridColDef[] = [
    {
      field: 'student_name',
      headerName: '',
      renderCell: list => {
        if (!list.row.student_nursing_record) {
          return <>기록없음</>;
        }

        const recordTypes = list.row.student_nursing_record.map(
          (type: any) => type.record_type
        );

        return (
          <Box sx={{ borderBottom: '3px solid black', width: '100%' }}>
            <Stack direction="row" mt={1}>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 'bold',
                  color: `#083766`,
                }}
              >
                {list.row.student_name}
              </Typography>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 'bold',
                  color: '#00000052',
                  margin: '0 5px 0 5px',
                }}
              >
                |
              </Typography>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 'bold',
                  color: `#083766`,
                }}
              >
                {list.row.student_no}
              </Typography>
            </Stack>

            {recordTypes.map((type: any, i: number) => {
              const catecorys = list.row.student_nursing_record[i].content;
              const jsonCategory = JSON.parse(catecorys);
              // version 은 NANDA 버전 메타값이라 기록 항목이 아니다
              const categoryKeys = (
                Object.keys(jsonCategory) as any[]
              ).filter(key => key !== 'version');

              const create_at = list.row.student_nursing_record[i].record_time;

              if (type === 0) {
                return (
                  <Box sx={{ margin: '0 0 5px 20px' }}>
                    <Stack direction="row" mt={0.5} mb={1}>
                      <Typography
                        sx={{
                          fontSize: '13px',
                          fontWeight: 500,
                        }}
                      >
                        Nanda
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '12px',
                          fontWeight: 500,
                          color: '#00000052',
                          marginLeft: '10px',
                        }}
                      >
                        {create_at}
                      </Typography>
                    </Stack>
                    {categoryKeys.map((title: string) => {
                      return (
                        <Fragment key={title}>
                          <Typography
                            component="li"
                            variant="caption"
                            fontWeight="bold"
                            sx={{
                              margin: '0 10px 0 10px',
                            }}
                          >
                            {fieldLabel(type, title)}
                          </Typography>
                          <Typography
                            // component="li"
                            variant="caption"
                            sx={{
                              pl: 1,
                              whiteSpace: 'pre-wrap',
                              marginLeft: '20px',
                            }}
                          >
                            {jsonCategory[title]}
                          </Typography>
                        </Fragment>
                      );
                    })}
                  </Box>
                );
              } else if (type === 1) {
                return (
                  <Box
                    sx={{
                      margin: '0 0 5px 20px',
                      borderTop: '1px solid lightgray',
                      width: '80vw',
                    }}
                  >
                    <Stack direction="row" mt={1} mb={1}>
                      <Typography
                        sx={{
                          fontSize: '13px',
                          fontWeight: 500,
                        }}
                      >
                        SOAPIE
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '12px',
                          fontWeight: 500,
                          color: '#00000052',
                          marginLeft: '10px',
                        }}
                      >
                        {create_at}
                      </Typography>
                    </Stack>
                    {categoryKeys.map((title: string) => {
                      return (
                        <Fragment key={title}>
                          <Typography
                            component="li"
                            variant="caption"
                            fontWeight="bold"
                            sx={{
                              margin: '0 10px 0 10px',
                            }}
                          >
                            {fieldLabel(type, title)}
                          </Typography>
                          <Typography
                            // component="li"
                            variant="caption"
                            sx={{
                              pl: 1,
                              whiteSpace: 'pre-wrap',
                              marginLeft: '20px',
                            }}
                          >
                            {jsonCategory[title]}
                          </Typography>
                        </Fragment>
                      );
                    })}
                  </Box>
                );
              } else if (type === 2) {
                return (
                  <Box
                    sx={{
                      margin: '0 0 5px 20px',
                      borderTop: '1px solid lightgray',
                      width: '80vw',
                    }}
                  >
                    <Stack direction="row" mt={1} mb={1}>
                      <Typography
                        sx={{
                          fontSize: '13px',
                          fontWeight: 500,
                        }}
                      >
                        Focus DAR
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '12px',
                          fontWeight: 500,
                          color: '#00000052',
                          marginLeft: '10px',
                        }}
                      >
                        {create_at}
                      </Typography>
                    </Stack>
                    {categoryKeys.map((title: string) => {
                      return (
                        <Fragment key={title}>
                          <Typography
                            component="li"
                            variant="caption"
                            fontWeight="bold"
                            sx={{
                              margin: '0 10px 0 10px',
                            }}
                          >
                            {fieldLabel(type, title)}
                          </Typography>
                          <Typography
                            // component="li"
                            variant="caption"
                            sx={{
                              pl: 1,
                              whiteSpace: 'pre-wrap',
                              marginLeft: '20px',
                            }}
                          >
                            {jsonCategory[title]}
                          </Typography>
                        </Fragment>
                      );
                    })}
                  </Box>
                );
              } else if (type === 3) {
                return (
                  <Box
                    sx={{
                      margin: '0 0 5px 20px',
                      borderTop: '1px solid lightgray',
                      width: '80vw',
                    }}
                  >
                    <Stack direction="row" mt={1} mb={1}>
                      <Typography
                        sx={{
                          fontSize: '13px',
                          fontWeight: 500,
                        }}
                      >
                        서술기록
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '12px',
                          fontWeight: 500,
                          color: '#00000052',
                          marginLeft: '10px',
                        }}
                      >
                        {create_at}
                      </Typography>
                    </Stack>
                    {categoryKeys.map((title: string) => {
                      return (
                        <Fragment key={title}>
                          <Typography
                            component="li"
                            variant="caption"
                            fontWeight="bold"
                            sx={{
                              margin: '0 10px 0 10px',
                            }}
                          >
                            서술기록 Narrative Notes
                          </Typography>
                          <Box sx={{ marginLeft: '30px' }}>
                            <QuillViewer
                              content={jsonCategory[title] ?? ''}
                              variant="caption"
                              emptyMessage=""
                            />
                          </Box>
                        </Fragment>
                      );
                    })}
                  </Box>
                );
              } else if (type === 4) {
                return (
                  <Box
                    sx={{
                      margin: '0 0 5px 20px',
                      borderTop: '1px solid lightgray',
                      width: '80vw',
                    }}
                  >
                    <Stack direction="row" mt={1} mb={1}>
                      <Typography
                        sx={{
                          fontSize: '13px',
                          fontWeight: 500,
                        }}
                      >
                        간호과정
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '12px',
                          fontWeight: 500,
                          color: '#00000052',
                          marginLeft: '10px',
                        }}
                      >
                        {create_at}
                      </Typography>
                    </Stack>
                    {categoryKeys.map((title: string) => {
                      if (title === 'diagnosisRelate') {
                        return (
                          <Fragment key={title}>
                            <Typography
                              component="li"
                              variant="caption"
                              fontWeight="bold"
                              sx={{
                                margin: '0 10px 0 10px',
                              }}
                            >
                              간호진단 Diagnosis
                            </Typography>
                            <Typography
                              // component="li"
                              variant="caption"
                              sx={{
                                pl: 1,
                                whiteSpace: 'pre-wrap',
                                marginLeft: '20px',
                              }}
                            >
                              {jsonCategory[title]}
                            </Typography>
                            <br />
                            <Typography
                              // component="li"
                              variant="caption"
                              sx={{
                                pl: 1,
                                whiteSpace: 'pre-wrap',
                                marginLeft: '20px',
                              }}
                            >
                              와/과 관련된
                            </Typography>
                            <br />
                          </Fragment>
                        );
                      } else if (title === 'diagnosis' && type === 4) {
                        return (
                          <Fragment key={title}>
                            <Typography
                              // component="li"
                              variant="caption"
                              sx={{
                                pl: 1,
                                whiteSpace: 'pre-wrap',
                                marginLeft: '20px',
                              }}
                            >
                              {jsonCategory[title]}
                            </Typography>
                          </Fragment>
                        );
                      } else
                        return (
                          <Fragment key={title}>
                            <Typography
                              component="li"
                              variant="caption"
                              fontWeight="bold"
                              sx={{
                                margin: '0 10px 0 10px',
                              }}
                            >
                              {fieldLabel(type, title)}
                            </Typography>
                            <Typography
                              // component="li"
                              variant="caption"
                              sx={{
                                pl: 1,
                                whiteSpace: 'pre-wrap',
                                marginLeft: '20px',
                              }}
                            >
                              {jsonCategory[title]}
                            </Typography>
                          </Fragment>
                        );
                    })}
                  </Box>
                );
              } else if (type === 6) {
                return (
                  <Box sx={{ margin: '0 0 5px 20px' }}>
                    <Stack direction="row" mt={0.5} mb={1}>
                      <Typography
                        sx={{
                          fontSize: '13px',
                          fontWeight: 500,
                        }}
                      >
                        OMAHA
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '12px',
                          fontWeight: 500,
                          color: '#00000052',
                          marginLeft: '10px',
                        }}
                      >
                        {create_at}
                      </Typography>
                    </Stack>
                    {categoryKeys.map((title: string) => {
                      if (title === 'sign_sx') {
                        return (
                          <Fragment key={title}>
                            <Typography
                              component="li"
                              variant="caption"
                              fontWeight="bold"
                              sx={{ marginLeft: '10px' }}
                            >
                              징후, 증상 (Sign, Symptom)
                            </Typography>
                            <Typography
                              component="li"
                              variant="caption"
                              sx={{
                                pl: 1,
                                whiteSpace: 'pre-wrap',
                                marginLeft: '12px',
                                marginBottom: '-20px',
                              }}
                            >
                              {jsonCategory[title].map(
                                (sign: string, i: number) => {
                                  if (i === jsonCategory[title].length - 1) {
                                    return sign;
                                  }
                                  return sign + ', ';
                                }
                              )}
                            </Typography>
                            <br />
                          </Fragment>
                        );
                      } else if (title === 'sign_sx_etc') {
                        return (
                          <Fragment key={title}>
                            <Typography
                              variant="caption"
                              sx={{
                                whiteSpace: 'pre-wrap',
                                color: '#0000008e',
                                marginBottom: '23px',
                                marginLeft: '40px',
                              }}
                            >
                              - {jsonCategory[title]}
                            </Typography>
                          </Fragment>
                        );
                      } else if (title === 'omaha_target1') {
                        return (
                          <Fragment key={title}>
                            <Typography
                              component="li"
                              variant="caption"
                              fontWeight="bold"
                              sx={{ marginLeft: '10px' }}
                            >
                              중재방안 (Intrvention Scheme)
                            </Typography>
                            <Typography
                              component="li"
                              variant="caption"
                              marginLeft={'20px'}
                              sx={{ fontSize: '12px' }}
                            >
                              보건교육, 상담과 지도 (Teaching, Guidance, and
                              Counseling) - 목표 및 대상별 정보 (Targets and
                              Client-Specific Information)
                            </Typography>
                            <Typography
                              // component="li"
                              variant="caption"
                              sx={{
                                pl: 1,
                                whiteSpace: 'pre-wrap',
                                color: '#0000008e',
                                marginLeft: '30px',
                              }}
                            >
                              - {jsonCategory[title]}
                            </Typography>
                          </Fragment>
                        );
                      } else if (title === 'omaha_target2') {
                        return (
                          <Fragment key={title}>
                            <Typography
                              component="li"
                              variant="caption"
                              marginLeft={'20px'}
                              sx={{ fontSize: '12px' }}
                            >
                              처치와 절차 (Treatments and Procedures) - 목표 및
                              대상별 정보 (Targets and Client-Specific
                              Information)
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                pl: 1,
                                whiteSpace: 'pre-wrap',
                                color: '#0000008e',
                                marginLeft: '30px',
                              }}
                            >
                              - {jsonCategory[title]}
                            </Typography>
                          </Fragment>
                        );
                      } else if (title === 'omaha_target3') {
                        return (
                          <Fragment key={title}>
                            <Typography
                              component="li"
                              variant="caption"
                              marginLeft={'20px'}
                              sx={{ fontSize: '12px' }}
                            >
                              사례관리 (Case Management) - 목표 및 대상별 정보
                              (Targets and Client-Specific Information)
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                pl: 1,
                                whiteSpace: 'pre-wrap',
                                color: '#0000008e',
                                marginLeft: '30px',
                              }}
                            >
                              - {jsonCategory[title]}
                            </Typography>
                          </Fragment>
                        );
                      } else if (title === 'omaha_target4') {
                        return (
                          <Fragment key={title}>
                            <Typography
                              component="li"
                              variant="caption"
                              marginLeft={'20px'}
                              sx={{ fontSize: '12px' }}
                            >
                              감독 (Surveillance) - 목표 및 대상별 정보 (Targets
                              and Client-Specific Information)
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                pl: 1,
                                whiteSpace: 'pre-wrap',
                                color: '#0000008e',
                                marginLeft: '30px',
                              }}
                            >
                              - {jsonCategory[title]}
                            </Typography>
                          </Fragment>
                        );
                      } else if (title === 'omaha_knowledge_selct') {
                        return (
                          <Fragment key={title}>
                            <Typography
                              component="li"
                              variant="caption"
                              fontWeight="bold"
                            >
                              결과에 대한 문제측정척도 (Problem Rating Scale for
                              Outcomes)
                            </Typography>
                            <Typography
                              component="li"
                              variant="caption"
                              marginLeft={'20px'}
                              sx={{ fontSize: '12px' }}
                            >
                              지식 (Knowledge)
                            </Typography>
                            <Typography
                              component="li"
                              variant="caption"
                              sx={{
                                pl: 1,
                                whiteSpace: 'pre-wrap',
                                color: '#0000008e',
                                marginLeft: '30px',
                              }}
                            >
                              {jsonCategory[title]}
                            </Typography>
                          </Fragment>
                        );
                      } else if (title === 'omaha_knowledge') {
                        return (
                          <Fragment key={title}>
                            <Typography
                              variant="caption"
                              sx={{
                                pl: 1,
                                whiteSpace: 'pre-wrap',
                                color: '#0000008e',
                                marginLeft: '50px',
                              }}
                            >
                              - {jsonCategory[title]}
                            </Typography>
                          </Fragment>
                        );
                      } else if (title === 'omaha_behavior_selct') {
                        return (
                          <Fragment key={title}>
                            <Typography
                              component="li"
                              variant="caption"
                              marginLeft={'20px'}
                              sx={{ fontSize: '12px' }}
                            >
                              행동 (Behavior)
                            </Typography>
                            <Typography
                              component="li"
                              variant="caption"
                              sx={{
                                pl: 1,
                                whiteSpace: 'pre-wrap',
                                color: '#0000008e',
                                marginLeft: '30px',
                              }}
                            >
                              {jsonCategory[title]}
                            </Typography>
                          </Fragment>
                        );
                      } else if (title === 'omaha_behavior') {
                        return (
                          <Fragment key={title}>
                            <Typography
                              variant="caption"
                              sx={{
                                pl: 1,
                                whiteSpace: 'pre-wrap',
                                color: '#0000008e',
                                marginLeft: '50px',
                              }}
                            >
                              - {jsonCategory[title]}
                            </Typography>
                          </Fragment>
                        );
                      } else if (title === 'omaha_status_selct') {
                        return (
                          <Fragment key={title}>
                            <Typography
                              component="li"
                              variant="caption"
                              marginLeft={'20px'}
                              sx={{ fontSize: '12px' }}
                            >
                              상태 (Status)
                            </Typography>
                            <Typography
                              component="li"
                              variant="caption"
                              sx={{
                                pl: 1,
                                whiteSpace: 'pre-wrap',
                                color: '#0000008e',
                                marginLeft: '30px',
                              }}
                            >
                              {jsonCategory[title]}
                            </Typography>
                          </Fragment>
                        );
                      } else if (title === 'omaha_status') {
                        return (
                          <Fragment key={title}>
                            <Typography
                              variant="caption"
                              sx={{
                                pl: 1,
                                whiteSpace: 'pre-wrap',
                                color: '#0000008e',
                                marginLeft: '50px',
                              }}
                            >
                              - {jsonCategory[title]}
                            </Typography>
                          </Fragment>
                        );
                      }
                      return (
                        <Fragment key={title}>
                          <Typography
                            component="li"
                            variant="caption"
                            fontWeight="bold"
                            sx={{
                              margin: '0 10px 0 10px',
                            }}
                          >
                            {fieldLabel(type, title)}
                          </Typography>
                          <Typography
                            // component="li"
                            variant="caption"
                            sx={{
                              pl: 1,
                              whiteSpace: 'pre-wrap',
                              marginLeft: '20px',
                            }}
                          >
                            {jsonCategory[title]}
                          </Typography>
                        </Fragment>
                      );
                    })}
                  </Box>
                );
              }
            })}
          </Box>
        );
      },
    },
  ];

  return (
    <MuiDataGrid
      headerHeight={0}
      columns={columns}
      loading={isLoading}
      onPageChange={onPageChange}
      page={page}
      pageSize={Number(pageSize) || 20}
      rows={list
        .filter(item => item.student_nursing_record?.length > 0)
        .map(item => ({ ...item, id: item.student_uuid }))}
      rowCount={totalCount}
      getRowHeight={() => 'auto'}
    />
  );
}

export default ContentsBody;
